// src/encounter/services/use-appointment-status.ts
//
// One appointment by id — the source of the encounter band's status pill (Arrived / In Progress /
// Completed) on the RELOAD and DEEP-LINK paths.
//
// WHY THIS EXISTS
// `EncounterShellPage` reads `appointmentStatus` off `location.state` (`EncounterPatientNav`), which
// only exists when the clinician arrived by TAPPING an appointment in Today/Calendar. Reload the
// encounter URL, or open it from a link, and router state is gone — so the status pill vanished
// entirely. Flutter never had that hole: `patient_info_widget` renders the status from the loaded
// appointment record, which the encounter fetches for itself.
//
// ENDPOINT — Flutter `scheduling_data_source_impl.dart:110-118` (`appointmentDetails`):
//   GET {appointmentAPI}/GetById/{id}?isFromUserApp=false
// `isFromUserApp` is `currentAppType == AppType.UA` there; the practitioner app is not UA, so it is
// always false for us. The response is the full `Appointment` model.
//
// TWO STATUS SHAPES, AND THIS ENDPOINT SENDS THE OTHER ONE (fixed 2026-08-24).
// Flutter's Appointment carries BOTH:
//   · `appointmentStatus`, a bare String from `json["appointmentStatus"]` (:386) — what the LIST
//     endpoints send, and what tap-time nav therefore carries;
//   · `status`, a `{code, display, definition}` object from `json["status"]` (:234) — what
//     GetById sends. Flutter's own active-appointment test reads `status?.code` (:524-526).
// This hook declared and read `appointmentStatus` only, so against GetById it was ALWAYS undefined:
// verified by replaying the live call on dev, which returns
// `status: {code: "Arrived", display: "Arrived", …}` and no `appointmentStatus` key at all.
// Consequence: on the RELOAD/DEEP-LINK path — exactly the path this hook exists to serve — the
// encounter's status control received no status and rendered NOTHING (Andrew, 2026-08-24: "the
// toggle of the appointment status not functional as expected"). It worked when you tapped through
// from Today/Calendar, because nav supplied the other shape.
// So `queryFn` now NORMALISES: `status.code` first, then the bare `appointmentStatus` for whatever
// still sends it. Consumers keep reading `.appointmentStatus` and none had to change.
//
// SCOPE + NAMING: deliberately narrow, and NOT called `useAppointmentDetails` — that name is taken
// by `src/services/scheduling/use-appointment-details.ts`, a different call (a `PreLoadAppointment`
// POST needing patientId + providerId + appointmentProfileId, for the calendar popup). Two hooks
// with one name would invite exactly the wrong one being imported. This returns only the slice the
// encounter chrome needs; the encounter already has patient, demographics and timing of its own.
//
// ROOM (added 2026-08-06): the same payload carries the appointment's ROOM, and this is the fetch
// the visit strip has been waiting on. `roomInfo` in Flutter reads the loaded appointment's nested
// room object (`appointment_model.dart:137` `Room? room` -> `Room { id, name }`, parsed at :257);
// the LIST endpoints ship only a bare `roomId`, which is why the strip could never render a room
// name before. Consumers must still treat it as optional — an appointment need not have a room, and
// a missing room means the strip shows a time-only cell, never a fabricated "Room 1".
//
// The `['appointment-details', …]` key PREFIX is shared with that hook on purpose: the status
// mutations (`encounter-status-control.tsx:79`, `appointment-status-manager.tsx`) invalidate the
// bare prefix, so this refetches when the status is written from anywhere.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { bookedSpecialtyId, bookedSpecialtyName } from '../lib/booked-specialty';
import { bookedVisitType } from '../lib/booked-visit-type';
import { patientFromAppointment, type AppointmentParticipantWire } from '../lib/appointment-patient';

/** The room this appointment is booked into (Flutter `Room`: `{ id, name }`). */
export interface EncounterAppointmentRoom {
  id?: string | null;
  /** Display name — the visit strip's ROOM eyebrow. Absent for unroomed appointments. */
  name?: string | null;
}

/** The slice of the appointment the encounter chrome consumes. */
export interface EncounterAppointmentStatus {
  /**
   * Appointment status CODE, normalised from whichever shape the wire used (see the header):
   * `status.code` on GetById, or a bare `appointmentStatus` elsewhere.
   */
  appointmentStatus?: string | null;
  /** Nested room record — the ONLY source of a room NAME (list endpoints ship only `roomId`). */
  room?: EncounterAppointmentRoom | null;
  /**
   * The appointment's PROVIDER — Flutter's `appointment.provider`, which the generated note's
   * header credits: `Created by <avatar> <displayName>` (`generated_notes_widget.dart:135-161`).
   *
   * Lives in the FHIR-shaped `participant[]` array under the entry whose `referenceType` is
   * "Provider", verified against the live dev payload. There is no top-level `provider` key.
   */
  provider?: EncounterAppointmentProvider | null;
  /**
   * Appointment START, ISO. The generated note's header line 2 is Flutter's
   * `location • scDateFormat(startTime) • scTimeFormatSingleLetter(startTime)`
   * (`generated_notes_widget.dart:129`), and the page's only source for it was
   * `location.state.appointmentStart` — which exists ONLY on the tap-through path. On a reload
   * or a deep link the date and time silently vanished and the line rendered as the bare location.
   * Same hole, same cure, as the status pill this hook was created for.
   */
  start?: string | null;
  /** Appointment END, ISO. Closes the video join window (2 hours after it) on a reload or deep link. */
  end?: string | null;
  /**
   * The visit's BOOKED specialty GUID — the encounter-template `specialtyId` axis on the
   * DEEP-LINK path (PROD-2701). Router state carries it only when the clinician tapped the
   * appointment in a list; opened any other way, the template request used to go out with no
   * specialty and QuestionBank (PROD-2696) then could not rank the specialty template first.
   * Read with the SAME rule the list uses (`lib/booked-specialty.ts`): the appointment's own
   * `specialty.specialitySkillId` / `specialityId`, then the provider participant's. `null` when
   * none can be read — the template request then proceeds unconstrained, as before.
   */
  specialtyId?: string | null;
  /** PROD-2947 · the booked specialty's name (the visit's discipline), when the record carries one. */
  specialtyName?: string | null;
  /**
   * The visit's appointment TYPE code (`appointmentType.code`: ROUTINE / FOLLOWUP / WALKIN) — the
   * encounter-template `visitType` axis on the DEEP-LINK path (TS-4, PROD-2820), read with the same
   * rule the list uses (`lib/booked-visit-type.ts`). `null` when the payload carries none.
   */
  appointmentTypeCode?: string | null;
  /**
   * The booked appointment profile's reference id (`appointmentProfile.referenceId`) — sent beside the
   * visit type (TS-4; phase 2 resolves it to a practice visit type). `null` when absent or all-zero.
   */
  appointmentProfileId?: string | null;
  /**
   * The patient this appointment is for — the encounter's patient on the DEEP-LINK path
   * (PROD-2804). Router state carries the subject only on a list tap-through, and the encounter
   * GET has none until an encounter exists, so a refreshed or linked encounter for a not-yet-started
   * visit had NO patient id and rendered an empty chart. Read with `lib/appointment-patient.ts`
   * (the participant rule the appointment edit form uses). `null` when the payload names none.
   */
  patientId?: string | null;
  /** The participant patient's display name, when carried — the header's name until the patient
   *  detail read lands. `null` when absent (never a fabricated "Unknown Patient"). */
  patientName?: string | null;
  /**
   * The visit's REASON FOR VISIT label, `reasonCode.display` — what Flutter's note header reads
   * (`add_encounter_detail_widget.dart:276-280`, `appointment.reasonCode.display`) and the value
   * CreateEncounter copies onto the encounter as `reasonForVisitName` (`use-create-encounter.ts`).
   * PROD-3045 N4: the note header read the reason only from the by-appointment encounter query,
   * which never runs once an encounter id is known, so a physician who opened the visit saw no
   * reason while a nurse arriving later by link did. This fetch runs on every path. `null` when the
   * payload carries none (GetById fills it from the practice or master reason list).
   */
  reason?: string | null;
}

/** The provider slice the note header needs (Flutter `provider.displayName()` + `.photo`). */
export interface EncounterAppointmentProvider {
  name?: string | null;
  /** Avatar URL. A SAS-signed blob URL on dev, so treat it as opaque and short-lived. */
  photo?: string | null;
}

/** The raw payload, carrying both status shapes Flutter's model knows about. */
interface AppointmentStatusWire {
  appointmentStatus?: string | null;
  start?: string | null;
  end?: string | null;
  status?: { code?: string | null } | null;
  room?: EncounterAppointmentRoom | null;
  /** TS-4: the visit-type axis and the phase-2 profile pointer. Read only through `bookedVisitType`. */
  appointmentType?: { code?: string | null } | null;
  appointmentProfile?: { referenceId?: string | null } | null;
  /** Scrips.Core `ReasonCode` `{ code, definition, display }` (AppointmentService GetById). */
  reasonCode?: { display?: string | null } | null;
  participant?:
    | (AppointmentParticipantWire & {
        // The provider node also carries the provider's speciality — read only through
        // `bookedSpecialtyId`, as the fallback behind the appointment's own booked specialty.
        provider?: EncounterAppointmentProvider | null;
      })[]
    | null;
}

/**
 * Fetch the appointment behind this encounter.
 *
 * `enabled` is false without an id.
 *
 * Callers should prefer tap-time nav state for the STATUS when they have it — that value is already
 * correct and needs no round trip. They should NOT gate the whole fetch on it, though: nav never
 * carries the room, so skipping the request on the tap-time path leaves the visit strip roomless.
 */
export function useAppointmentStatus(appointmentId: string | undefined, enabled = true) {
  return useQuery<EncounterAppointmentStatus | null>({
    queryKey: ['appointment-details', appointmentId],
    enabled: enabled && !!appointmentId,
    // The status changes through the visit (arrived -> in progress -> completed) and the
    // EncounterStatusControl writes it, so this must not serve a long-stale value; the control's
    // own mutation invalidates the key.
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      const wire = await get<AppointmentStatusWire | null>(
        `/api/Appointment/GetById/${encodeURIComponent(appointmentId!)}?isFromUserApp=false`,
        { signal },
      );
      if (!wire) return null;
      const provider =
        (wire.participant ?? []).find(
          (p) => (p?.referenceType ?? '').toLowerCase() === 'provider',
        )?.provider ?? null;
      const patient = patientFromAppointment(wire.participant);
      const booked = bookedVisitType(wire);
      return {
        // `status.code` is what THIS endpoint sends; the bare field is kept as a fallback rather
        // than dropped, because the list endpoints do send it and one normaliser beats two readers.
        appointmentStatus: wire.status?.code ?? wire.appointmentStatus ?? null,
        room: wire.room ?? null,
        start: wire.start ?? null,
        end: wire.end ?? null,
        provider,
        specialtyId: bookedSpecialtyId(wire, provider) ?? null,
        specialtyName: bookedSpecialtyName(wire, provider) ?? null,
        appointmentTypeCode: booked.visitType ?? null,
        appointmentProfileId: booked.appointmentProfileId ?? null,
        patientId: patient?.id ?? null,
        patientName: patient?.name ?? null,
        reason: wire.reasonCode?.display?.trim() || null,
      };
    },
  });
}
